import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';

export type AlertTone = 'info' | 'warn' | 'danger';

interface AlertBannerProps {
  tone: AlertTone;
  icon: React.ComponentProps<typeof Feather>['name'];
  title: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
}

// Tone palettes from the handoff (warm, never cold). System errors go in a
// banner; input errors go under the field.
const TONES: Record<AlertTone, { bg: string; border: string; icon: string; text: string }> = {
  danger: { bg: '#fdeeed', border: '#f2c9c5', icon: '#ba1a1a', text: '#7a1a17' },
  warn: { bg: '#fdf4e3', border: '#eddcb5', icon: '#a06a1a', text: '#6b4a12' },
  info: { bg: '#f3ece2', border: '#e2d3c3', icon: '#864e2c', text: '#52443c' },
};

export const AlertBanner: React.FC<AlertBannerProps> = ({
  tone,
  icon,
  title,
  message,
  actionLabel,
  onAction,
}) => {
  const c = TONES[tone];
  return (
    <View style={[styles.banner, { backgroundColor: c.bg, borderColor: c.border }]}>
      <Feather name={icon} size={18} color={c.icon} style={styles.icon} />
      <View style={styles.body}>
        <Text style={[styles.title, { color: c.text }]}>{title}</Text>
        {message ? <Text style={[styles.message, { color: c.text }]}>{message}</Text> : null}
        {actionLabel ? (
          <TouchableOpacity onPress={onAction} style={styles.action} hitSlop={6}>
            <Text style={[styles.actionText, { color: c.icon }]}>{actionLabel}</Text>
            <Feather name="arrow-right" size={14} color={c.icon} />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    gap: 11,
    padding: 13,
    paddingHorizontal: 15,
    borderRadius: 12,
    borderWidth: 1,
  },
  icon: {
    marginTop: 1,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    marginBottom: 3,
  },
  message: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 19,
    opacity: 0.9,
  },
  action: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 8,
  },
  actionText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
  },
});
